import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Container from './ui/Container';
import Button from './ui/Button';
import { ArrowRight } from './ui/Icons';
import BookConsultationButton from './BookConsultationButton';
import HeroMontage from './HeroMontage';
import WorkStrip from './WorkStrip';

const HeroSection = styled.section`
  position: relative;
  overflow: hidden;
  padding: clamp(3rem, 7vw, 5.5rem) 0 clamp(4rem, 8vw, 6.5rem);
  background:
    linear-gradient(180deg, rgba(7, 12, 22, 0) 60%, var(--bg-0) 100%),
    linear-gradient(rgba(255, 255, 255, 0.025) 1px, transparent 1px) 0 0 / 64px 64px,
    linear-gradient(90deg, rgba(255, 255, 255, 0.025) 1px, transparent 1px) 0 0 / 64px 64px,
    var(--bg-0);
`;

const Grid = styled(Container)`
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  gap: clamp(2.5rem, 5vw, 4.5rem);
  align-items: center;

  @media (max-width: 959px) {
    grid-template-columns: 1fr;
  }
`;

const Copy = styled.div`
  max-width: 640px;
`;

const Eyebrow = styled.p`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  font-size: var(--fs-eyebrow);
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--accent);
  margin-bottom: 1.25rem;

  span[aria-hidden] {
    color: var(--slate-500);
  }
`;

const Title = styled.h1`
  font-size: clamp(2.35rem, 4.6vw, 3.9rem);
  font-weight: 700;
  line-height: 1.06;
  letter-spacing: -0.03em;

  em {
    font-style: normal;
    color: var(--accent);
  }
`;

const Sub = styled.p`
  margin-top: 1.5rem;
  font-size: clamp(1.0625rem, 1.4vw, 1.1875rem);
  line-height: 1.65;
  color: var(--text-2);
  max-width: 58ch;

  strong {
    color: var(--text-1);
    font-weight: 600;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 2rem;

  @media (max-width: 420px) {
    > * {
      flex: 1 1 100%;
    }
  }
`;

const Visual = styled(motion.div)`
  position: relative;
  padding-bottom: 1rem;

  @media (max-width: 959px) {
    display: none;
  }
`;

const Hero: React.FC = () => (
  <HeroSection aria-labelledby="hero-title">
    <Grid>
      <Copy>
        <Eyebrow>
          Software studio <span aria-hidden="true">·</span> Puerto Rico <span aria-hidden="true">·</span> Remote-ready
        </Eyebrow>
        <Title id="hero-title">
          Custom websites, apps, and cloud platforms, <em>built, secured, and run by one team.</em>
        </Title>
        <Sub>
          <strong>CuttingEdge DevOps</strong> designs, develops, and maintains software for businesses in Puerto Rico
          and beyond: marketing sites, web and mobile apps, cloud infrastructure, and the security around them. The
          people you meet on the first call are the ones who ship.
        </Sub>
        <Actions>
          <BookConsultationButton size="lg" />
          <Button to="/projects" variant="secondary" size="lg">
            See our work <ArrowRight />
          </Button>
        </Actions>
        <WorkStrip />
      </Copy>

      <Visual
        data-reveal=""
        initial={{ y: 16 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.45, ease: 'easeOut', delay: 0.06 }}
      >
        <HeroMontage />
      </Visual>
    </Grid>
  </HeroSection>
);

export default Hero;
